import React, { useState, useRef, useEffect } from 'react';
import { parse } from '../../../util/urlHelpers';
import { subcategoriesFor, subcategoryLabel } from '../../../data/occasions';

import css from './SubThemeFilters.module.css';

// Restructured 2026-05-15: reads from src/data/occasions.js (single
// source of truth) instead of the standalone sub-themes.json file.
// The catalog stores keys + labels separately; this component
// stores the key in pub_subTheme so URLs are stable across label
// edits.
//
// Known limitation (pre-existing, not changed in this PR): the
// `pub_subTheme` filter doesn't match any actual listing field
// today — listings store `publicData.sub_categories` (array), not
// `subTheme` (string). The chip UI works but the filter is
// effectively a no-op until either the param is renamed to
// `pub_sub_categories` or Sharetribe Console gets a `subTheme`
// search index. Tracked for a follow-up.

const SubThemeFilters = ({
  location,
  onFilterClick,
  currentCategory,
  selectedSubTheme,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const queryParams = parse(location.search);
  const category =
    currentCategory ||
    queryParams.pub_categoryLevel1 ||
    queryParams.category;
  // Catalog lookup honors legacy aliases — listings with
  // event_type=cultural surface Holiday's subcategories.
  const subThemes = subcategoriesFor(category);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Don't render if no category or no sub-themes
  if (!category || subThemes.length === 0) {
    return null;
  }

  const handlePillClick = subKey => {
    if (onFilterClick) {
      if (selectedSubTheme === subKey) {
        onFilterClick('pub_subTheme', null);
      } else {
        onFilterClick('pub_subTheme', subKey);
      }
    }
    setIsOpen(false);
  };

  const handleClear = () => {
    if (onFilterClick) {
      onFilterClick('pub_subTheme', null);
    }
    setIsOpen(false);
  };

  // Selected value may still be a legacy label string from older
  // URLs (e.g. "🦖 Dinosaur"); fall through to the raw value if
  // the catalog doesn't know it.
  const selectedLabel = selectedSubTheme
    ? subcategoryLabel(category, selectedSubTheme) || selectedSubTheme
    : 'Occasion';
  const isActive = !!selectedSubTheme;

  return (
    <div className={css.root} ref={rootRef}>
      <button
        type="button"
        className={`${css.triggerPill} ${isActive ? css.triggerPillActive : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>🎉 {selectedLabel}</span>
        <span className={css.arrow}>▾</span>
      </button>

      {isOpen && (
        <div className={css.popover}>
          <h4 className={css.popoverTitle}>Occasion</h4>
          <div className={css.pillGrid}>
            {subThemes.map(sub => (
              <button
                key={sub.key}
                type="button"
                className={`${css.optionPill} ${
                  selectedSubTheme === sub.key ? css.optionPillActive : ''
                }`}
                onClick={() => handlePillClick(sub.key)}
              >
                {sub.label}
              </button>
            ))}
          </div>
          {selectedSubTheme && (
            <button type="button" className={css.clearBtn} onClick={handleClear}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SubThemeFilters;
